package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.marketing.AuthField
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.Space
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Form
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Where a password-reset email lands.
 *
 * The token arrives in the query string, so the page never asks for it — someone who has clicked a
 * link from their inbox should only have to choose a password.
 */
@Page("/reset-password")
@Composable
fun ResetPasswordPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val token = ctx.route.queryParams["token"].orEmpty()

    var password by remember { mutableStateOf("") }
    var confirm by remember { mutableStateOf("") }
    var submitted by remember { mutableStateOf(false) }
    var saving by remember { mutableStateOf(false) }
    var done by remember { mutableStateOf(false) }
    var apiError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) { document.title = tr("reset_password.choose_a_new_password_lingvo", "Choose a new password — Lingvo") }

    // Mirrors the server so the rules are learned before a round trip, never instead of one — and
    // it mirrors *both* rules now. `validatePassword` also refuses anything over 72 bytes, because
    // bcrypt does, so a passphrase or a short password of accented or CJK characters used to pass
    // here, cost a round trip and come back as a server error: the one outcome a mirror exists to
    // prevent. The guard below and the message here read the same function, so they cannot drift.
    val passwordError = if (submitted) newPasswordProblem(password) else null
    val confirmError = if (submitted && confirm != password) {
        tr("reset_password.both_passwords_must_match", "Both passwords must match.")
    } else {
        null
    }

    fun submit() {
        submitted = true
        if (newPasswordProblem(password) != null || confirm != password || saving) return
        apiError = null
        // Before the launch, not inside it — the way every sibling page does it. A coroutine body
        // does not run until the dispatcher reaches it, so setting the flag in there left both this
        // guard and the button's `enabled` false for the whole dispatch window: a double-click, or
        // Enter in the field plus a click, sent two POSTs carrying the same token. The second loses
        // the conditional UPDATE in `PasswordResetRepo.consume` and returns 400, so the page
        // announced "This reset link has already been used" immediately after successfully using it.
        saving = true
        scope.launch {
            try {
                ApiClient.confirmPasswordReset(token, password)
                // The server revokes every session in the same transaction as the password change,
                // this browser's included, so the token still in local storage is already dead.
                // Dropping it here makes the client agree with the server now rather than on the
                // next 401 — and clearing it is the whole point of the flow for somebody who reset
                // because a device was lost.
                Session.logout()
                done = true
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: ApiException) {
                apiError = failure.message
            } catch (failure: Throwable) {
                apiError = tr(
                    "reset_password.couldnt_reach_lingvo_check_your_connection",
                    "Couldn't reach Lingvo. Check your connection and retry.",
                )
            } finally {
                saving = false
            }
        }
    }

    AuthPageScaffold(
        title = if (done) {
            t("reset_password.password_changed", "Password changed")
        } else {
            t("reset_password.choose_a_new_password", "Choose a new password")
        },
        subtitle = when {
            done -> t("reset_password.you_can_sign_in_with_it", "You can sign in with it now.")
            token.isBlank() -> t("reset_password.this_link_is_missing_its_token", "This link is missing its token.")
            else -> t("reset_password.pick_something_you_have_not_used", "Pick something you have not used here before.")
        },
        alternatePrompt = t("reset_password.remembered_it", "Remembered it?"),
        alternateAction = t("reset_password.back_to_log_in", "Back to log in"),
        alternatePath = "/login",
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            when {
                done -> {
                    // What the reset actually did, on the only screen that can say it. The confirm
                    // route revokes, in the same transaction as the password change, every API
                    // token, every session on every device, and every agent connection this person
                    // created — so a developer who reset a forgotten password walks away with a CI
                    // pipeline that fails on its next `lingvo push` and nothing anywhere to explain
                    // why. Source of truth: `PasswordResetRoutes`' /auth/password-reset/confirm.
                    LAlert(
                        title = t("reset_password.your_other_credentials_were_revoked", "Your other credentials were revoked"),
                        description = t(
                            "reset_password.every_device_is_signed_out_and",
                            "Every device is signed out, and every API token and agent connection " +
                                "you created has been revoked. Issue a new token from " +
                                "Settings → API tokens before your next lingvo push.",
                        ),
                        modifier = Modifier.fillMaxWidth(),
                        variant = LAlertVariant.WARNING,
                        // It appeared because they just pressed "Change password", which is the one
                        // case LAlert reserves a live region for.
                        live = true,
                    )
                    LButton(
                        label = t("reset_password.go_to_log_in", "Go to log in"),
                        onClick = { ctx.router.tryRoutingTo("/login") },
                        modifier = Modifier.fillMaxWidth(),
                    )
                }

                token.isBlank() -> LAlert(
                    title = t("reset_password.this_link_is_incomplete", "This link is incomplete"),
                    description = t(
                        "reset_password.open_the_link_from_your_email",
                        "Open the link from your email again, or request a new one from " +
                            "the log-in page.",
                    ),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.WARNING,
                    // Deliberately not live: this notice is on the page at first paint, and a live
                    // region present at load talks over the heading that says which page this is.
                )

                else -> Form(
                    attrs = Modifier.fillMaxWidth().toAttrs {
                        attr("novalidate", "")
                        onSubmit { event ->
                            event.preventDefault()
                            submit()
                        }
                    },
                ) {
                    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
                        AuthField(
                            value = password,
                            onValueChange = { password = it; apiError = null },
                            label = t("reset_password.new_password", "New password"),
                            placeholderText = t("reset_password.at_least_8_characters", "At least 8 characters"),
                            inputType = InputType.Password,
                            name = "new-password",
                            autocomplete = "new-password",
                            error = passwordError,
                            enabled = !saving,
                            onBlur = {},
                            onCommit = ::submit,
                        )
                        AuthField(
                            value = confirm,
                            onValueChange = { confirm = it; apiError = null },
                            label = t("reset_password.confirm_password", "Confirm password"),
                            placeholderText = t("reset_password.type_it_again", "Type it again"),
                            inputType = InputType.Password,
                            name = "confirm-password",
                            autocomplete = "new-password",
                            error = confirmError,
                            enabled = !saving,
                            onBlur = {},
                            onCommit = ::submit,
                        )
                        if (apiError != null) {
                            LAlert(
                                title = t("reset_password.couldnt_change_your_password", "Couldn't change your password"),
                                description = apiError.orEmpty(),
                                modifier = Modifier.fillMaxWidth(),
                                variant = LAlertVariant.DANGER,
                                // It exists because the reader just pressed "Change password", and
                                // the field errors beside it already announce themselves. Without
                                // this the only failure a screen reader never hears is the one that
                                // came back from the server.
                                live = true,
                            )
                        }
                        LButton(
                            label = t("reset_password.change_password", "Change password"),
                            onClick = ::submit,
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !saving,
                            loading = saving,
                        )
                    }
                }
            }
        }
    }
}

/**
 * The server's two password rules, both of them, before a round trip.
 *
 * The byte ceiling is not a second guess at the length one. bcrypt hashes at most 72 bytes and the
 * library `AuthRoutes.validatePassword` uses refuses more, while a character outside ASCII costs
 * two to four of those bytes — so "at least 8 characters" and "at most 72 bytes" are both true of a
 * password nobody would call long, and only the server used to say so.
 */
private fun newPasswordProblem(password: String): String? = when {
    password.length < MIN_PASSWORD_LENGTH ->
        tr("reset_password.use_at_least_8_characters", "Use at least 8 characters.")

    password.encodeToByteArray().size > MAX_PASSWORD_BYTES ->
        tr(
            "reset_password.that_password_is_too_long_the",
            "That password is too long. The limit is 72 bytes, and accented or non-Latin " +
                "characters count for more than one byte each.",
        )

    else -> null
}

private const val MIN_PASSWORD_LENGTH = 8

/** bcrypt hashes at most 72 bytes; `AuthRoutes.validatePassword` refuses anything longer. */
private const val MAX_PASSWORD_BYTES = 72
